import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, MailCheck } from "lucide-react";
import AuthSplit from "layouts/auth/types/AuthSplit";
import { Button } from "components/ui/Button";
import env from "variables/config";
import { AuthHeading, EMAIL_RE, Field, FormAlert, linkClass } from "views/auth/authParts";

function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    const requestLink = (e?: FormEvent) => {
        e?.preventDefault();
        const normalizedEmail = email.trim().toLowerCase();
        const nextEmailError = !normalizedEmail
            ? "Saisissez votre email professionnel."
            : !EMAIL_RE.test(normalizedEmail)
            ? "Cet email ne semble pas valide."
            : null;
        setEmailError(nextEmailError);
        setFormError(null);
        if (nextEmailError) return;

        setLoading(true);
        axios
            .post(`${env.apiUrl}/auth/forgot-password`, { email: normalizedEmail })
            .then(() => setSent(true))
            .catch((error: any) => {
                // An unknown email answers 404. We show the same confirmation either way, so the
                // page never tells a stranger which emails have an account.
                if (error?.response?.status === 404) setSent(true);
                else setFormError("Envoi impossible pour le moment. Réessayez dans quelques instants.");
            })
            .finally(() => setLoading(false));
    };

    if (sent) {
        return (
            <AuthSplit>
                <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-subtle text-accent">
                    <MailCheck className="h-5 w-5" />
                </div>
                <AuthHeading
                    title="Vérifiez votre boîte mail"
                    subtitle="Si un compte existe pour cette adresse, vous allez recevoir un lien pour choisir un nouveau mot de passe."
                />
                <p className="-mt-4 mb-8 rounded-lg border border-border bg-surface-sunken px-3 py-3 font-mono text-sm text-ink">{email.trim().toLowerCase()}</p>

                <Button variant="primary" size="lg" className="w-full" onClick={() => navigate("/auth/login")}>
                    Retour à la connexion
                </Button>

                <p className="mt-6 text-center text-sm text-ink-muted">
                    Rien reçu ? Pensez à vos spams, ou{" "}
                    <button type="button" onClick={() => setSent(false)} className={linkClass}>
                        renvoyez le lien
                    </button>
                    .
                </p>
            </AuthSplit>
        );
    }

    return (
        <AuthSplit>
            <Link to="/auth/login" className={`mb-8 inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink ${linkClass}`}>
                <ArrowLeft className="h-4 w-4" />
                Retour à la connexion
            </Link>

            <AuthHeading title="Mot de passe oublié ?" subtitle="Indiquez votre email, nous vous envoyons un lien pour en choisir un nouveau." />

            {formError && <FormAlert>{formError}</FormAlert>}

            <form onSubmit={requestLink} noValidate className="space-y-4">
                <Field
                    id="email"
                    label="Email professionnel"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    autoFocus
                    placeholder="prenom@entreprise.fr"
                    value={email}
                    error={emailError}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError(null);
                        setFormError(null);
                    }}
                />

                <Button type="submit" variant="accent" size="lg" disabled={loading} aria-busy={loading} className="w-full">
                    {loading ? (
                        <>
                            <Loader2 className="h-4 w-4 motion-safe:animate-spin" />
                            Envoi en cours…
                        </>
                    ) : (
                        <>
                            Recevoir le lien
                            <ArrowRight className="h-4 w-4" />
                        </>
                    )}
                </Button>
            </form>
        </AuthSplit>
    );
}

export default ForgotPassword;
